import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import staffService from '../services/staffService';
import performanceService from '../services/performanceService';
import { toast } from 'sonner';
import { Plus, ChartLineUp, Users, Target, ChatCircleText, Warning } from '@phosphor-icons/react';

const emptyForm = { staff_id: '', review_period: '', overall_rating: '', strengths: '', development_goals: '', training_needs: '' };
const emptyBulkForm = { review_period: '', staff_ids: [] };
const emptyGoalForm = { staff_id: '', review_id: '', title: '', description: '', target_metric: '', target_value: '', current_value: '', unit: '', due_date: '' };
const emptyPipForm = { staff_id: '', review_id: '', reason: '', goals: '', start_date: '', end_date: '' };
const GOAL_STATUSES = ['not_started', 'on_track', 'at_risk', 'achieved', 'missed'];
const ADMIN_ROLES = ['super_admin', 'school_admin', 'hr'];

function ReviewRow({ review, onSubmit, onAcknowledge, canManageFeedback, feedbackExpanded, onToggleFeedback }) {
  return (
    <tr>
      <td>{review.staff_name}</td>
      <td>{review.review_period}</td>
      <td>{review.self_rating ? `${review.self_rating} / 5` : '-'}</td>
      <td>{review.overall_rating ? `${review.overall_rating} / 5` : '-'}</td>
      <td className="text-capitalize">{review.status.replace('_', ' ')}</td>
      <td>
        {(review.status === 'draft' || review.status === 'self_assessed') && (
          <button className="btn btn-sm btn-outline-primary" onClick={() => onSubmit(review)}>Submit</button>
        )}
        {review.status === 'submitted' && onAcknowledge && (
          <button className="btn btn-sm btn-outline-success" onClick={() => onAcknowledge(review)}>Acknowledge</button>
        )}
        {canManageFeedback && (
          <button className="btn btn-sm btn-outline-info ms-1" onClick={() => onToggleFeedback(review)}>
            {feedbackExpanded ? 'Hide Feedback' : '360 Feedback'}
          </button>
        )}
      </td>
    </tr>
  );
}

function HRPerformancePage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [tab, setTab] = useState(isAdmin ? 'team' : 'mine');
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [selfForm, setSelfForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Staff goals (OKRs)
  const [goals, setGoals] = useState([]);
  const [goalForm, setGoalForm] = useState(emptyGoalForm);
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalProgressDrafts, setGoalProgressDrafts] = useState({});

  // 360-degree feedback
  const [feedbackRequests, setFeedbackRequests] = useState([]);
  const [feedbackSubmitDrafts, setFeedbackSubmitDrafts] = useState({});
  const [expandedFeedbackReviewId, setExpandedFeedbackReviewId] = useState(null);
  const [feedbackRequestForms, setFeedbackRequestForms] = useState({});
  const [aggregatedFeedback, setAggregatedFeedback] = useState({});

  // Performance Improvement Plans
  const [pips, setPips] = useState([]);
  const [pipForm, setPipForm] = useState(emptyPipForm);
  const [pipSaving, setPipSaving] = useState(false);
  const [pipCheckInDrafts, setPipCheckInDrafts] = useState({});
  const [pipCloseDrafts, setPipCloseDrafts] = useState({});

  const load = async () => {
    const calls = [
      performanceService.listReviews(),
      staffService.getMyProfile().catch(() => ({ staff_id: null })),
      performanceService.listGoals().catch(() => []),
      performanceService.listPips().catch(() => []),
      performanceService.myFeedbackRequests().catch(() => []),
    ];
    if (isAdmin) calls.push(staffService.listStaff({ limit: 100 }));
    const [reviewData, profile, goalData, pipData, feedbackRequestData, staffData] = await Promise.all(calls);
    setReviews(reviewData || []);
    setMyProfile(profile);
    setGoals(goalData || []);
    setPips(pipData || []);
    setFeedbackRequests(feedbackRequestData || []);
    if (staffData) setStaff(staffData?.items || []);
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load performance reviews')).finally(() => setLoading(false)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      await performanceService.createReview({ ...form, overall_rating: form.overall_rating ? Number(form.overall_rating) : null });
      setForm(emptyForm); await load(); toast.success('Performance review created');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create review'); }
    finally { setSaving(false); }
  };

  const toggleBulkStaff = (staffId) => {
    setBulkForm(f => ({
      ...f,
      staff_ids: f.staff_ids.includes(staffId) ? f.staff_ids.filter(id => id !== staffId) : [...f.staff_ids, staffId],
    }));
  };

  const toggleAllBulkStaff = () => {
    setBulkForm(f => ({ ...f, staff_ids: f.staff_ids.length === staff.length ? [] : staff.map(s => s.id) }));
  };

  const launchCycle = async (event) => {
    event.preventDefault();
    if (!bulkForm.review_period || bulkForm.staff_ids.length === 0) {
      toast.error('Enter a review period and select at least one staff member');
      return;
    }
    setBulkSaving(true);
    try {
      const result = await performanceService.bulkLaunchReviews(bulkForm);
      toast.success(`Launched ${result.created_count} review(s)${result.skipped_count ? `, skipped ${result.skipped_count}` : ''}`);
      setBulkForm(emptyBulkForm);
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to launch review cycle'); }
    finally { setBulkSaving(false); }
  };

  const transition = async (review, status) => {
    try { await performanceService.changeStatus(review.id, status); await load(); toast.success(`Review ${status}`); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not update review'); }
  };

  const submitMySelfAssessment = async (review) => {
    const data = selfForm[review.id] || {};
    try {
      await performanceService.submitSelfAssessment(review.id, {
        self_rating: data.self_rating ? Number(data.self_rating) : null,
        self_comments: data.self_comments || null,
      });
      toast.success('Self-assessment submitted');
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit self-assessment'); }
  };

  const acknowledgeMyReview = async (review) => {
    try { await performanceService.acknowledgeReview(review.id); toast.success('Review acknowledged'); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to acknowledge review'); }
  };

  // ---- Staff goals (OKRs) ----

  const createGoal = async (event) => {
    event.preventDefault(); setGoalSaving(true);
    try {
      await performanceService.createGoal({
        ...goalForm,
        review_id: goalForm.review_id || null,
        target_value: goalForm.target_value !== '' ? Number(goalForm.target_value) : null,
        current_value: goalForm.current_value !== '' ? Number(goalForm.current_value) : null,
      });
      setGoalForm(emptyGoalForm); await load(); toast.success('Goal created');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create goal'); }
    finally { setGoalSaving(false); }
  };

  const updateGoalProgress = async (goal) => {
    const draft = goalProgressDrafts[goal.id] || {};
    try {
      await performanceService.updateGoalProgress(goal.id, {
        current_value: draft.current_value !== undefined && draft.current_value !== '' ? Number(draft.current_value) : undefined,
        status: draft.status || undefined,
      });
      toast.success('Goal progress updated');
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update goal progress'); }
  };

  // ---- 360-degree feedback ----

  const toggleFeedbackPanel = async (review) => {
    if (expandedFeedbackReviewId === review.id) { setExpandedFeedbackReviewId(null); return; }
    setExpandedFeedbackReviewId(review.id);
    try {
      const data = await performanceService.getAggregatedFeedback(review.id);
      setAggregatedFeedback(prev => ({ ...prev, [review.id]: data }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to load feedback'); }
  };

  const toggleFeedbackGiver = (reviewId, staffId) => {
    setFeedbackRequestForms(prev => {
      const current = prev[reviewId] || { staff_ids: [], relationship: 'peer' };
      const staff_ids = current.staff_ids.includes(staffId)
        ? current.staff_ids.filter(id => id !== staffId)
        : [...current.staff_ids, staffId];
      return { ...prev, [reviewId]: { ...current, staff_ids } };
    });
  };

  const toggleAllFeedbackGivers = (review) => {
    const eligible = staff.filter(s => s.id !== review.staff_id).map(s => s.id);
    setFeedbackRequestForms(prev => {
      const current = prev[review.id] || { staff_ids: [], relationship: 'peer' };
      const staff_ids = current.staff_ids.length === eligible.length ? [] : eligible;
      return { ...prev, [review.id]: { ...current, staff_ids } };
    });
  };

  const submitFeedbackRequest = async (review) => {
    const requestForm = feedbackRequestForms[review.id] || { staff_ids: [], relationship: 'peer' };
    if (requestForm.staff_ids.length === 0) { toast.error('Select at least one feedback giver'); return; }
    try {
      await performanceService.requestFeedback(review.id, {
        review_id: review.id,
        feedback_giver_staff_ids: requestForm.staff_ids,
        relationship: requestForm.relationship,
      });
      toast.success('Feedback requested');
      setFeedbackRequestForms(prev => ({ ...prev, [review.id]: { staff_ids: [], relationship: 'peer' } }));
      const data = await performanceService.getAggregatedFeedback(review.id);
      setAggregatedFeedback(prev => ({ ...prev, [review.id]: data }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to request feedback'); }
  };

  const submitMyFeedback = async (request) => {
    const draft = feedbackSubmitDrafts[request.id] || {};
    try {
      await performanceService.submitFeedback(request.id, {
        rating: draft.rating ? Number(draft.rating) : null,
        strengths: draft.strengths || null,
        areas_for_improvement: draft.areas_for_improvement || null,
        comments: draft.comments || null,
      });
      toast.success('Feedback submitted');
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit feedback'); }
  };

  // ---- Performance Improvement Plans ----

  const createPip = async (event) => {
    event.preventDefault(); setPipSaving(true);
    try {
      await performanceService.createPip({ ...pipForm, review_id: pipForm.review_id || null });
      setPipForm(emptyPipForm); await load(); toast.success('Performance improvement plan created');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create PIP'); }
    finally { setPipSaving(false); }
  };

  const checkInPip = async (pip) => {
    const notes = pipCheckInDrafts[pip.id];
    if (!notes) { toast.error('Enter check-in notes'); return; }
    try {
      await performanceService.checkInPip(pip.id, notes);
      setPipCheckInDrafts({ ...pipCheckInDrafts, [pip.id]: '' });
      toast.success('Check-in added');
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add check-in'); }
  };

  const closePip = async (pip) => {
    const draft = pipCloseDrafts[pip.id] || {};
    if (!draft.status) { toast.error('Select a closing status'); return; }
    try {
      await performanceService.closePip(pip.id, { status: draft.status, outcome: draft.outcome || null });
      toast.success('PIP closed');
      await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to close PIP'); }
  };

  if (loading) return <Layout><div className="loading">Loading performance reviews...</div></Layout>;

  const myStaffId = myProfile?.staff_id;
  const myReviews = myStaffId ? reviews.filter(r => r.staff_id === myStaffId) : [];
  const myTeamReviews = myStaffId ? reviews.filter(r => r.manager_id === myStaffId) : [];
  const isManager = (myProfile?.direct_report_count || 0) > 0;
  const myPips = myStaffId ? pips.filter(p => p.staff_id === myStaffId) : [];
  const canManageHr = isAdmin || isManager;

  const tabs = [
    ...(isAdmin ? [['team', 'All Reviews']] : []),
    ...(myStaffId ? [['mine', 'My Review']] : []),
    ...(isManager ? [['reports', 'My Team']] : []),
    ['goals', 'Goals'],
    ...(feedbackRequests.length > 0 ? [['feedback', 'My Feedback Requests']] : []),
    ...(canManageHr || myPips.length > 0 ? [['pips', 'Improvement Plans']] : []),
  ];

  const renderFeedbackPanel = (review) => {
    const requestForm = feedbackRequestForms[review.id] || { staff_ids: [], relationship: 'peer' };
    const eligibleStaff = staff.filter(s => s.id !== review.staff_id);
    const agg = aggregatedFeedback[review.id];
    return (
      <tr>
        <td colSpan={6}>
          <div className="p-2">
            <strong>Request 360 feedback</strong>
            <div className="row g-2 align-items-end mt-1 mb-3">
              <div className="col-12">
                <button type="button" className="btn btn-sm btn-outline-secondary mb-2" onClick={() => toggleAllFeedbackGivers(review)}>
                  {requestForm.staff_ids.length === eligibleStaff.length ? 'Deselect All' : 'Select All'} ({requestForm.staff_ids.length} of {eligibleStaff.length} selected)
                </button>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6, maxHeight: 150, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                  {eligibleStaff.map(s => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                      <input type="checkbox" checked={requestForm.staff_ids.includes(s.id)} onChange={() => toggleFeedbackGiver(review.id, s.id)} style={{ width: 'auto' }} />
                      {s.first_name} {s.last_name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-md-4">
                <select
                  className="form-select form-select-sm"
                  value={requestForm.relationship}
                  onChange={e => setFeedbackRequestForms(prev => ({ ...prev, [review.id]: { ...requestForm, relationship: e.target.value } }))}
                >
                  <option value="peer">Peer</option>
                  <option value="subordinate">Subordinate</option>
                  <option value="manager">Manager</option>
                  <option value="self">Self</option>
                </select>
              </div>
              <div className="col-md-4">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => submitFeedbackRequest(review)}>Request feedback</button>
              </div>
            </div>

            <strong>Aggregated feedback</strong>
            {!agg && <p className="text-muted mb-0 mt-1">Loading...</p>}
            {agg && agg.requested === 0 && <p className="text-muted mb-0 mt-1">No feedback requested yet.</p>}
            {agg && agg.requested > 0 && (
              <>
                <p className="mb-2 mt-1 text-muted">
                  {agg.submitted} of {agg.requested} submitted
                  {agg.average_rating ? ` · Average rating ${agg.average_rating} / 5` : ''}
                </p>
                {(agg.entries || []).map((entry, idx) => (
                  <div key={idx} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between">
                      <span className="text-capitalize">{entry.relationship}</span>
                      <span>{entry.rating ? `${entry.rating} / 5` : '-'}</span>
                    </div>
                    {entry.strengths && <p className="mb-1"><strong>Strengths:</strong> {entry.strengths}</p>}
                    {entry.areas_for_improvement && <p className="mb-1"><strong>Areas for improvement:</strong> {entry.areas_for_improvement}</p>}
                    {entry.comments && <p className="mb-0"><strong>Comments:</strong> {entry.comments}</p>}
                  </div>
                ))}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderReviewsTable = (list) => (
    <div className="table-responsive">
      <table className="table">
        <thead><tr><th>Staff</th><th>Period</th><th>Self Rating</th><th>Manager Rating</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {list.map(review => (
            <React.Fragment key={review.id}>
              <ReviewRow
                review={review}
                onSubmit={(r) => transition(r, 'submitted')}
                canManageFeedback
                feedbackExpanded={expandedFeedbackReviewId === review.id}
                onToggleFeedback={toggleFeedbackPanel}
              />
              {expandedFeedbackReviewId === review.id && renderFeedbackPanel(review)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1><ChartLineUp className="me-2" />Staff performance</h1>
            <p>Two-sided reviews — staff self-assess, managers evaluate, both sign off.</p>
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="d-flex gap-2 mb-3 flex-wrap">
            {tabs.map(([key, label]) => (
              <button key={key} type="button" className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(key)}>{label}</button>
            ))}
          </div>
        )}

        {tab === 'team' && isAdmin && (
          <>
            <section className="card mb-4">
              <div className="card-header"><strong><Users className="me-2" />Launch a review cycle</strong></div>
              <div className="card-body">
                <form onSubmit={launchCycle} className="row g-3 mb-3">
                  <div className="col-md-4">
                    <input className="form-control" placeholder="Review period e.g. 2026 Term 1" value={bulkForm.review_period} onChange={e => setBulkForm({ ...bulkForm, review_period: e.target.value })} required />
                  </div>
                  <div className="col-12">
                    <button type="button" className="btn btn-sm btn-outline-secondary mb-2" onClick={toggleAllBulkStaff}>
                      {bulkForm.staff_ids.length === staff.length ? 'Deselect All' : 'Select All'} ({bulkForm.staff_ids.length} of {staff.length} selected)
                    </button>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6, maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {staff.map(s => (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                          <input type="checkbox" checked={bulkForm.staff_ids.includes(s.id)} onChange={() => toggleBulkStaff(s.id)} style={{ width: 'auto' }} />
                          {s.first_name} {s.last_name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="col-12">
                    <button className="btn btn-primary" disabled={bulkSaving}>{bulkSaving ? 'Launching...' : `Launch Review Cycle (${bulkForm.staff_ids.length})`}</button>
                  </div>
                </form>
              </div>
            </section>

            <section className="card mb-4">
              <div className="card-header"><strong><Plus className="me-2" />New performance review</strong></div>
              <div className="card-body">
                <form onSubmit={create} className="row g-3">
                  <div className="col-md-4"><select className="form-select" value={form.staff_id} onChange={e => setForm({ ...form, staff_id: e.target.value })} required><option value="">Select staff member</option>{staff.map(item => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}</select></div>
                  <div className="col-md-4"><input className="form-control" placeholder="Review period e.g. 2026 Term 1" value={form.review_period} onChange={e => setForm({ ...form, review_period: e.target.value })} required /></div>
                  <div className="col-md-4"><select className="form-select" value={form.overall_rating} onChange={e => setForm({ ...form, overall_rating: e.target.value })}><option value="">Overall rating</option>{[1, 2, 3, 4, 5].map(value => <option key={value} value={value}>{value} / 5</option>)}</select></div>
                  <div className="col-md-4"><textarea className="form-control" rows="3" placeholder="Strengths" value={form.strengths} onChange={e => setForm({ ...form, strengths: e.target.value })} /></div>
                  <div className="col-md-4"><textarea className="form-control" rows="3" placeholder="Development goals" value={form.development_goals} onChange={e => setForm({ ...form, development_goals: e.target.value })} /></div>
                  <div className="col-md-4"><textarea className="form-control" rows="3" placeholder="Training needs" value={form.training_needs} onChange={e => setForm({ ...form, training_needs: e.target.value })} /></div>
                  <div className="col-12"><button className="btn btn-primary" disabled={saving}>Create draft</button></div>
                </form>
              </div>
            </section>

            <section className="card">
              <div className="card-header"><strong>Review history</strong></div>
              <div className="card-body">
                {renderReviewsTable(reviews)}
                {reviews.length === 0 && <p className="text-muted mb-0">No performance reviews yet.</p>}
              </div>
            </section>
          </>
        )}

        {tab === 'mine' && (
          <section className="card">
            <div className="card-header"><strong>My performance reviews</strong></div>
            <div className="card-body">
              {myReviews.length === 0 && <p className="text-muted mb-0">No reviews assigned to you yet.</p>}
              {myReviews.map(review => (
                <div key={review.id} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>{review.review_period}</strong>
                    <span className="badge text-capitalize" style={{ background: '#e5e7eb', color: '#111' }}>{review.status.replace('_', ' ')}</span>
                  </div>

                  {(review.status === 'draft' || review.status === 'self_assessed') ? (
                    <div className="row g-2">
                      <div className="col-md-3">
                        <select
                          className="form-select"
                          value={selfForm[review.id]?.self_rating ?? review.self_rating ?? ''}
                          onChange={e => setSelfForm({ ...selfForm, [review.id]: { ...selfForm[review.id], self_rating: e.target.value } })}
                        >
                          <option value="">Self-rating</option>
                          {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} / 5</option>)}
                        </select>
                      </div>
                      <div className="col-md-7">
                        <textarea
                          className="form-control" rows="2" placeholder="Your self-assessment comments"
                          value={selfForm[review.id]?.self_comments ?? review.self_comments ?? ''}
                          onChange={e => setSelfForm({ ...selfForm, [review.id]: { ...selfForm[review.id], self_comments: e.target.value } })}
                        />
                      </div>
                      <div className="col-md-2">
                        <button type="button" className="btn btn-primary w-100" onClick={() => submitMySelfAssessment(review)}>Submit</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="mb-1"><strong>Your self-rating:</strong> {review.self_rating ? `${review.self_rating} / 5` : 'Not submitted'} — {review.self_comments || ''}</p>
                      <p className="mb-1"><strong>Manager's rating:</strong> {review.overall_rating ? `${review.overall_rating} / 5` : 'Pending'}</p>
                      {review.strengths && <p className="mb-1"><strong>Strengths:</strong> {review.strengths}</p>}
                      {review.development_goals && <p className="mb-1"><strong>Development goals:</strong> {review.development_goals}</p>}
                      {review.status === 'submitted' && (
                        <button type="button" className="btn btn-sm btn-outline-success mt-2" onClick={() => acknowledgeMyReview(review)}>Acknowledge</button>
                      )}
                      {review.status === 'acknowledged' && <span className="text-muted">Acknowledged {review.acknowledged_at ? new Date(review.acknowledged_at).toLocaleDateString() : ''}</span>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === 'reports' && isManager && (
          <section className="card">
            <div className="card-header"><strong>My team's reviews</strong></div>
            <div className="card-body">
              {renderReviewsTable(myTeamReviews)}
              {myTeamReviews.length === 0 && <p className="text-muted mb-0">No reviews for your direct reports yet.</p>}
            </div>
          </section>
        )}

        {tab === 'goals' && (
          <>
            {canManageHr && (
              <section className="card mb-4">
                <div className="card-header"><strong><Target className="me-2" />New staff goal</strong></div>
                <div className="card-body">
                  <form onSubmit={createGoal} className="row g-3">
                    <div className="col-md-4">
                      <select className="form-select" value={goalForm.staff_id} onChange={e => setGoalForm({ ...goalForm, staff_id: e.target.value })} required>
                        <option value="">Select staff member</option>
                        {staff.map(item => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <select className="form-select" value={goalForm.review_id} onChange={e => setGoalForm({ ...goalForm, review_id: e.target.value })}>
                        <option value="">Link to a review (optional)</option>
                        {reviews.filter(r => r.staff_id === goalForm.staff_id).map(r => <option key={r.id} value={r.id}>{r.review_period}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4"><input className="form-control" placeholder="Goal title" value={goalForm.title} onChange={e => setGoalForm({ ...goalForm, title: e.target.value })} required /></div>
                    <div className="col-md-6"><textarea className="form-control" rows="2" placeholder="Description" value={goalForm.description} onChange={e => setGoalForm({ ...goalForm, description: e.target.value })} /></div>
                    <div className="col-md-6"><input className="form-control" placeholder="Target metric e.g. Students mentored" value={goalForm.target_metric} onChange={e => setGoalForm({ ...goalForm, target_metric: e.target.value })} /></div>
                    <div className="col-md-3"><input type="number" className="form-control" placeholder="Target value" value={goalForm.target_value} onChange={e => setGoalForm({ ...goalForm, target_value: e.target.value })} /></div>
                    <div className="col-md-3"><input type="number" className="form-control" placeholder="Current value" value={goalForm.current_value} onChange={e => setGoalForm({ ...goalForm, current_value: e.target.value })} /></div>
                    <div className="col-md-3"><input className="form-control" placeholder="Unit e.g. %" value={goalForm.unit} onChange={e => setGoalForm({ ...goalForm, unit: e.target.value })} /></div>
                    <div className="col-md-3"><input type="date" className="form-control" value={goalForm.due_date} onChange={e => setGoalForm({ ...goalForm, due_date: e.target.value })} /></div>
                    <div className="col-12"><button className="btn btn-primary" disabled={goalSaving}>{goalSaving ? 'Saving...' : 'Create goal'}</button></div>
                  </form>
                </div>
              </section>
            )}

            <section className="card">
              <div className="card-header"><strong>Goals</strong></div>
              <div className="card-body">
                {goals.length === 0 && <p className="text-muted mb-0">No goals yet.</p>}
                {goals.map(goal => {
                  const draft = goalProgressDrafts[goal.id] || {};
                  const canEdit = isAdmin || isManager || goal.staff_id === myStaffId;
                  return (
                    <div key={goal.id} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>{goal.title}</strong>
                        <span className="badge text-capitalize" style={{ background: '#e5e7eb', color: '#111' }}>{(goal.status || 'not_started').replace('_', ' ')}</span>
                      </div>
                      {goal.staff_name && <p className="mb-1 text-muted">{goal.staff_name}</p>}
                      {goal.description && <p className="mb-1">{goal.description}</p>}
                      <p className="mb-2 text-muted">
                        {goal.target_metric && `${goal.target_metric}: `}
                        {goal.current_value ?? 0}{goal.unit || ''} / {goal.target_value ?? '—'}{goal.unit || ''}
                        {goal.due_date && ` · Due ${new Date(goal.due_date).toLocaleDateString()}`}
                      </p>
                      {canEdit && (
                        <div className="row g-2">
                          <div className="col-md-3">
                            <input
                              type="number" className="form-control form-control-sm" placeholder="Current value"
                              value={draft.current_value ?? ''}
                              onChange={e => setGoalProgressDrafts({ ...goalProgressDrafts, [goal.id]: { ...draft, current_value: e.target.value } })}
                            />
                          </div>
                          <div className="col-md-4">
                            <select
                              className="form-select form-select-sm"
                              value={draft.status ?? goal.status ?? ''}
                              onChange={e => setGoalProgressDrafts({ ...goalProgressDrafts, [goal.id]: { ...draft, status: e.target.value } })}
                            >
                              <option value="">Status</option>
                              {GOAL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={() => updateGoalProgress(goal)}>Update</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {tab === 'feedback' && (
          <section className="card">
            <div className="card-header"><strong><ChatCircleText className="me-2" />My feedback requests</strong></div>
            <div className="card-body">
              {feedbackRequests.length === 0 && <p className="text-muted mb-0">No feedback requests right now.</p>}
              {feedbackRequests.map(request => {
                const draft = feedbackSubmitDrafts[request.id] || {};
                return (
                  <div key={request.id} className="border rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong className="text-capitalize">{request.relationship} feedback{request.subject_staff_name ? ` for ${request.subject_staff_name}` : ''}</strong>
                      <span className="badge" style={{ background: request.submitted ? '#d1fae5' : '#e5e7eb', color: '#111' }}>{request.submitted ? 'Submitted' : 'Pending'}</span>
                    </div>
                    {!request.submitted && (
                      <div className="row g-2">
                        <div className="col-md-3">
                          <select
                            className="form-select form-select-sm"
                            value={draft.rating ?? ''}
                            onChange={e => setFeedbackSubmitDrafts({ ...feedbackSubmitDrafts, [request.id]: { ...draft, rating: e.target.value } })}
                          >
                            <option value="">Rating</option>
                            {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v} / 5</option>)}
                          </select>
                        </div>
                        <div className="col-md-4"><textarea className="form-control form-control-sm" rows="2" placeholder="Strengths" value={draft.strengths ?? ''} onChange={e => setFeedbackSubmitDrafts({ ...feedbackSubmitDrafts, [request.id]: { ...draft, strengths: e.target.value } })} /></div>
                        <div className="col-md-4"><textarea className="form-control form-control-sm" rows="2" placeholder="Areas for improvement" value={draft.areas_for_improvement ?? ''} onChange={e => setFeedbackSubmitDrafts({ ...feedbackSubmitDrafts, [request.id]: { ...draft, areas_for_improvement: e.target.value } })} /></div>
                        <div className="col-md-4"><textarea className="form-control form-control-sm" rows="2" placeholder="Comments" value={draft.comments ?? ''} onChange={e => setFeedbackSubmitDrafts({ ...feedbackSubmitDrafts, [request.id]: { ...draft, comments: e.target.value } })} /></div>
                        <div className="col-12"><button type="button" className="btn btn-sm btn-primary" onClick={() => submitMyFeedback(request)}>Submit feedback</button></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {tab === 'pips' && (
          <>
            {canManageHr && (
              <section className="card mb-4">
                <div className="card-header"><strong><Warning className="me-2" />New performance improvement plan</strong></div>
                <div className="card-body">
                  <form onSubmit={createPip} className="row g-3">
                    <div className="col-md-4">
                      <select className="form-select" value={pipForm.staff_id} onChange={e => setPipForm({ ...pipForm, staff_id: e.target.value })} required>
                        <option value="">Select staff member</option>
                        {staff.map(item => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <select className="form-select" value={pipForm.review_id} onChange={e => setPipForm({ ...pipForm, review_id: e.target.value })}>
                        <option value="">Link to a review (optional)</option>
                        {reviews.filter(r => r.staff_id === pipForm.staff_id).map(r => <option key={r.id} value={r.id}>{r.review_period}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3"><input type="date" className="form-control" value={pipForm.start_date} onChange={e => setPipForm({ ...pipForm, start_date: e.target.value })} required /></div>
                    <div className="col-md-3"><input type="date" className="form-control" value={pipForm.end_date} onChange={e => setPipForm({ ...pipForm, end_date: e.target.value })} required /></div>
                    <div className="col-md-6"><textarea className="form-control" rows="2" placeholder="Reason" value={pipForm.reason} onChange={e => setPipForm({ ...pipForm, reason: e.target.value })} required /></div>
                    <div className="col-md-6"><textarea className="form-control" rows="2" placeholder="Goals" value={pipForm.goals} onChange={e => setPipForm({ ...pipForm, goals: e.target.value })} required /></div>
                    <div className="col-12"><button className="btn btn-primary" disabled={pipSaving}>{pipSaving ? 'Saving...' : 'Create PIP'}</button></div>
                  </form>
                </div>
              </section>
            )}

            <section className="card">
              <div className="card-header"><strong>Performance improvement plans</strong></div>
              <div className="card-body">
                {(canManageHr ? pips : myPips).length === 0 && <p className="text-muted mb-0">No performance improvement plans.</p>}
                {(canManageHr ? pips : myPips).map(pip => {
                  const closed = pip.status === 'completed' || pip.status === 'escalated' || pip.status === 'closed';
                  const closeDraft = pipCloseDrafts[pip.id] || {};
                  return (
                    <div key={pip.id} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>{pip.staff_name || 'Staff member'}</strong>
                        <span className="badge text-capitalize" style={{ background: '#e5e7eb', color: '#111' }}>{(pip.status || 'active').replace('_', ' ')}</span>
                      </div>
                      <p className="mb-1"><strong>Reason:</strong> {pip.reason}</p>
                      <p className="mb-1"><strong>Goals:</strong> {pip.goals}</p>
                      <p className="mb-2 text-muted">{new Date(pip.start_date).toLocaleDateString()} — {new Date(pip.end_date).toLocaleDateString()}</p>
                      {pip.check_in_notes && <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}><strong>Check-ins:</strong> {pip.check_in_notes}</p>}
                      {pip.outcome && <p className="mb-2"><strong>Outcome:</strong> {pip.outcome}</p>}

                      {canManageHr && !closed && (
                        <>
                          <div className="row g-2 mb-2">
                            <div className="col-md-8">
                              <textarea
                                className="form-control form-control-sm" rows="2" placeholder="Add a check-in note"
                                value={pipCheckInDrafts[pip.id] || ''}
                                onChange={e => setPipCheckInDrafts({ ...pipCheckInDrafts, [pip.id]: e.target.value })}
                              />
                            </div>
                            <div className="col-md-4">
                              <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={() => checkInPip(pip)}>Add check-in</button>
                            </div>
                          </div>

                          <div className="row g-2">
                            <div className="col-md-4">
                              <select
                                className="form-select form-select-sm"
                                value={closeDraft.status || ''}
                                onChange={e => setPipCloseDrafts({ ...pipCloseDrafts, [pip.id]: { ...closeDraft, status: e.target.value } })}
                              >
                                <option value="">Close as...</option>
                                <option value="completed">Completed</option>
                                <option value="escalated">Escalated</option>
                                <option value="closed">Closed</option>
                              </select>
                            </div>
                            <div className="col-md-5">
                              <input
                                className="form-control form-control-sm" placeholder="Outcome"
                                value={closeDraft.outcome || ''}
                                onChange={e => setPipCloseDrafts({ ...pipCloseDrafts, [pip.id]: { ...closeDraft, outcome: e.target.value } })}
                              />
                            </div>
                            <div className="col-md-3">
                              <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => closePip(pip)}>Close PIP</button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

export default HRPerformancePage;
